import { Link } from 'react-router-dom'
import AnalysisStatusBadge from './AnalysisStatusBadge'

/**
 * Helper function to format relative time
 */
function formatRelativeTime(isoString) {
  const date = new Date(isoString)
  const now = new Date()
  const diffMs = now - date
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'just now'
  if (diffMins < 60) return `${diffMins} minute${diffMins === 1 ? '' : 's'} ago`
  if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`
  if (diffDays === 1) return 'yesterday'
  return `${diffDays} days ago`
}

/**
 * Analysis card component for list/grid view
 * @param {Object} analysis - Analysis object
 * @param {boolean} priority - Highlight as priority (needs attention)
 * @param {boolean} error - Highlight as error (failed)
 */
function AnalysisCard({ analysis, priority = false, error = false }) {
  const progressPercent = analysis.current_step > 0 ? (analysis.current_step / 5) * 100 : 0

  return (
    <Link
      to={`/analyses/${analysis.id}`}
      className={`block bg-white rounded-lg shadow hover:shadow-md transition-shadow p-4 border-2 ${
        priority ? 'border-yellow-400' : error ? 'border-red-400' : 'border-transparent'
      }`}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <h3 className="font-semibold text-gray-900 text-lg truncate flex-1 mr-2">
          {analysis.name}
        </h3>
        <AnalysisStatusBadge status={analysis.status} />
      </div>

      {/* Project name */}
      <p className="text-sm text-gray-600 mb-3">{analysis.project_name}</p>

      {/* Processing progress bar */}
      {analysis.status === 'processing' && analysis.current_step > 0 && (
        <div className="mb-3">
          <div className="flex justify-between text-xs text-gray-600 mb-1">
            <span>Step {analysis.current_step}/5</span>
            <span>{Math.round(progressPercent)}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>
      )}

      {/* Awaiting input alert */}
      {analysis.status === 'awaiting_input' && (
        <div className="mb-3 text-yellow-700 text-sm bg-yellow-50 border border-yellow-200 rounded p-2">
          ⚠️ Waiting for your input at Step {analysis.current_step}
        </div>
      )}

      {/* Failed alert */}
      {analysis.status === 'failed' && (
        <div className="mb-3 text-red-700 text-sm bg-red-50 border border-red-200 rounded p-2">
          ✗ Failed at Step {analysis.current_step || 'initialization'}
        </div>
      )}

      {/* Completed status */}
      {analysis.status === 'completed' && (
        <div className="mb-3 text-green-700 text-sm">✓ All steps completed</div>
      )}

      {/* Footer */}
      <div className="text-xs text-gray-500 pt-2 border-t border-gray-200">
        Created {formatRelativeTime(analysis.created_at)}
      </div>
    </Link>
  )
}

export default AnalysisCard
